<template>
  <div>
    <v-container grid-list-xl fluid class="px-8 py-4">
      <div class="mb-3">
        <h1 class="fs-18 lh-18 text-uppercase mt-1 font-weight-700">
          IMPORT USER MAPPING
        </h1>
        <v-breadcrumbs class="px-0 py-2" :items="breadcrumbs"></v-breadcrumbs>
      </div>
      <v-row no-gutters align="center" justify="space-between" class="mb-3">
        <v-col cols="12" sm="6">
          <h1 class="fs-20 font-weight-bold bluedarken--text">
            Import User Mapping
          </h1>
        </v-col>
        <v-col cols="12" sm="auto" class="px-0">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" icon medium v-on="on">
                <v-icon color="bluedarken" class="icon-bold icon-large" large>
                  mdi-refresh
                </v-icon>
              </v-btn>
            </template>
            <span>
              Refresh
            </span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-card class="pb-7 pt-5 elevation-card rounded-10">
        <div class="mx-5 mb-7">
          <label class="darkblue--text fs-14">
            Import File
          </label>
          <vue-dropzone
            class="mt-2"
            :use-font-awesome="true"
            ref="myVueDropzone"
            id="dropzone"
            :options="dropzoneOptions"
            @vdropzone-complete="afterComplete"
            @vdropzone-reset="afterRemoved"
          ></vue-dropzone>

          <v-btn
            class="secondblue mt-5 white--text"
            block
            id="convertButton"
            :disabled="isDisabled"
            :loading="loading"
            @click="importFile"
          >
            Import
          </v-btn>

          <v-btn
            class="thirdblue mt-3 white--text"
            block
            id="downloadButton"
            @click="getTemplate"
          >
            Download Template
          </v-btn>
        </div>

        <v-simple-table
          v-if="isFileImported"
          fixed-header
          class="table-taxonomy nav-table sshcf-table mx-5"
        >
          <template v-slot:default>
            <VuePerfectScrollbar
              class="drawer-menu--scroll"
              :settings="scrollSettings"
            >
              <thead>
                <tr>
                  <template v-for="header in headers">
                    <th
                      :key="header.text"
                      class="white--text"
                      :class="{
                        [`text-${header.align}`]: true,
                        [header.customClass]: !!header.customClass
                      }"
                    >
                      <span>{{ header.text }}</span>
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in items" :key="index">
                  <template v-for="header in headers">
                    <td
                      :key="header.name"
                      :class="{
                        [header.tdClass]: true,
                        [`text-${header.align}`]: true,
                        [header.tdClass]: !!header.tdClass
                      }"
                    >
                      <span>
                        {{ header | tableRowValue(item) }}
                      </span>
                    </td>
                  </template>
                </tr>
                <tr v-if="!items.length">
                  <td :colspan="headers.length" class="text-center">
                    {{ loading ? 'Loading...' : 'No data available' }}
                  </td>
                </tr>
              </tbody>
            </VuePerfectScrollbar>
          </template>
        </v-simple-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import Swal from 'sweetalert2/dist/sweetalert2.js';

export default {
  components: {
    VuePerfectScrollbar,
    vueDropzone: vue2Dropzone
  },
  data() {
    return {
      dropzoneOptions: {
        url: 'https://httpbin.org/post',
        thumbnailWidth: 150,
        acceptedFiles: '.xlsx',
        maxFiles: 1,
        init: function() {
          this.on('addedfile', function() {
            if (this.files.length > 1) {
              this.removeFile(this.files[0]);
            }
          });
        },
        addRemoveLinks: true,
        dictDefaultMessage: `
            <i class="fas fa-camera"></i><span class="primary--text">Click or Drop Files Here to Upload</span>
          `
      },
      scrollSettings: {
        maxScrollbarLength: 340
      },
      breadcrumbs: [
        {
          text: 'Approval Management',
          disabled: true,
          href: 'breadcrumbs_dashboard'
        },
        {
          text: 'Import User Mapping',
          disabled: true,
          href: 'breadcrumbs_link_1'
        }
      ],
      headers: [
        { text: 'NIK', value: 'nik', align: 'left' },
        { text: 'Name', value: 'name' },
        { text: 'Email', value: 'user_email' },
        { text: 'Region', value: 'region' },
        { text: 'District', value: 'district' },
        { text: 'Group', value: 'group' },
        { text: 'Status', value: 'status' }
      ],
      fileUpload: null,
      isFileImported: false,
      isDisabled: true,
      items: [],
      loading: false,
      baseUrl: process.env.VUE_APP_BASE_URL
    };
  },
  methods: {
    afterComplete(file) {
      var fileType = file.upload.filename.split('.').pop();
      var fileTypes = ['xlsx', 'xls', 'XLS', 'XLSX'];
      if (fileTypes.includes(fileType)) {
        this.fileUpload = file;
        this.isDisabled = false;
        this.isFileImported = false;
      } else {
        Swal.fire(
          'Failed!',
          'Only file types document .xlsx and .xls are allowed.',
          'error'
        );
        this.$refs.myVueDropzone.removeAllFiles();
      }
    },
    afterRemoved() {
      this.isFileImported = false;
      this.fileUpload = null;
      this.isDisabled = true;
    },
    importFile() {
      Swal.fire({
        title: '',
        text: `Are you sure want to import this data?`,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then(result => {
        if (result.value) {
          this.uploadUser();
        }
      });
    },
    async uploadUser() {
      this.isFileImported = false;
      this.loading = true;
      let formData = new FormData();
      formData.append('user', this.fileUpload);
      await this.$store
        .dispatch('sshcp/importUser', formData)
        .then(data => {
          if (data.code === 200) {
            this.loading = false;
            this.$refs.myVueDropzone.removeAllFiles();
            this.items = data.data.result;
            this.fileUpload = null;
            this.isDisabled = true;
            this.isFileImported = true;
            Swal.fire('Success!', data.message.text_en, 'success');
          } else {
            this.loading = false;
            Swal.fire('Failed!', data.message.text_en, 'error');
          }
        })
        .catch(err => {
          this.loading = false;
          Swal.fire('Failed!', err.response.data.message.text_en, 'error');
        });
    },
    getTemplate() {
      window.open(
        this.baseUrl +
          '/v1/sshcp/user/mapping/template' +
          '?token=' +
          localStorage.getItem('app_token'),
        '_blank'
      );
    }
  }
};
</script>

<style>
.dropzoneHeight {
  height: 30vh;
}
.drawer-menu--scroll {
  max-height: 300px;
  overflow: auto;
}
.v-data-table.table-taxonomy.nav-table.sshcf-table {
  border-right: 0 !important;
  border-bottom: 0 !important;
}
.v-data-table.table-taxonomy.nav-table.sshcf-table thead,
.v-data-table.table-taxonomy.nav-table.sshcf-table td:last-child {
  border-right: thin solid rgba(0, 0, 0, 0.12) !important;
}
</style>
